import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux'

import CustomFieldInput from '../../../CustomFieldInput'

import { isUUID } from '../../../../../utilities'
import { AddErrorAlert } from '../../../../../slices/alert'
import { putUserMeta } from '../../../../../lib/api'
import { useBreakpoint } from '../../../../../providers/BreakpointProvider'
import { useCustomerContext } from '../../../../../providers/CustomerProvider'
import { useEditContext } from '../../../../../providers/EditProvider'
import { useMetaContext } from '../../../../../providers/MetaProvider'
import { selectAccess } from '../../../../../slices/accessSlice'
import { selectCustomFields } from '../../../../../slices/customFields'
import useCustomFieldGroups from '../../../../../hooks/useCustomFieldGroups'
import useCustomFieldValues from '@SonarCX/hooks/useCustomFieldValues'
import useStyles from './styles'

interface CustomFieldsSectionProps {
  user: any
  canEdit: boolean
  external: boolean
}
const CustomFieldsSection: React.FC<CustomFieldsSectionProps> = ({
  user,
  canEdit,
  external
}) => {
  const {
    meta = [],
    metaCache,
    setMeta,
    fetchMeta,
    updateMetaVal
  } = useMetaContext()
  const dispatch = useAppDispatch()
  const { handleChange, setOld, setIsSaving } = useCustomerContext()
  const { setForceSave } = useEditContext()
  const { isSmall } = useBreakpoint()
  const { loaded: accessLoaded } = useAppSelector(selectAccess)
  const { customFields } = useAppSelector(selectCustomFields)
  const { groups, hasFields } = useCustomFieldGroups(
    external ? 'external-user' : 'user',
    user.type_id
  )

  const [loaded, setLoaded] = React.useState(false)
  const [shouldSave, setShouldSave] = React.useState(false)

  const onValueChange = React.useCallback(() => {
    setForceSave(true)
  }, [setForceSave])

  const { handleValueChange } = useCustomFieldValues({
    meta,
    updateMetaVal,
    onValueChange
  })

  const classes = useStyles({
    canEdit,
    smallMedia: isSmall
  })

  // load in meta from hook
  React.useEffect(() => {
    if (!user?.id || !accessLoaded) {
      return
    }

    setMeta([])
    fetchMeta(user.id, true, true, user?.name).catch(err =>
      dispatch(AddErrorAlert({ err }))
    )
  }, [user, accessLoaded, setMeta, metaCache, fetchMeta, dispatch])

  // load in meta from hook
  React.useEffect(() => {
    if (!meta) {
      return
    }

    if (!loaded && meta.length) {
      // sets meta for team page
      setOld(meta)

      meta.forEach(({ key, value }) => {
        handleChange(value, key)
      })

      setLoaded(true)
    }
  }, [user, meta, setOld, handleChange, loaded, external, customFields])

  // Saves meta after blur
  React.useEffect(() => {
    if (!shouldSave) {
      return
    }

    const id = user?.id
    if (!id) {
      return
    }

    setShouldSave(false)

    // Only save values with UUID keys and convert empty strings to null
    const saveableMeta = meta
      .filter(obj => isUUID(obj.key))
      .map(({ key, value }) => {
        let val

        if (value instanceof Date) {
          val = value.toISOString()
        } else {
          val = value.toString().trim()
          if (val.length === 0) {
            val = null
          }
        }

        return { key, value: val }
      })

    setIsSaving(true)

    // Save meta
    putUserMeta(id, saveableMeta)
      .then(() => {
        setIsSaving(false)
      })
      .catch(err => {
        console.error('Error saving user meta', err)
      })
  }, [shouldSave, meta, user, setIsSaving])

  const handleBlur = React.useCallback(async () => {
    if (user) {
      setShouldSave(true)
    }
  }, [user])

  if (!hasFields) {
    return null
  }

  return (
    <>
      {groups.map((group, i) => {
        const fields = group.field_ids
          .map(id => {
            const field = customFields.find(field => field.id === id)
            if (!field) {
              return null
            }

            return (
              <CustomFieldInput
                key={field.id}
                field={field}
                value={meta.find(data => data.key === field.id)?.value ?? ''}
                canEdit={canEdit}
                handleChange={handleValueChange}
                handleBlur={handleBlur}
                timelineMode={true}
                meta={meta}
                shouldFilter
              />
            )
          })
          .filter(Boolean)

        if (fields.length === 0) {
          return null
        }

        return (
          <div key={`${group.id}-${i}`} className={classes.group}>
            <div className={classes.title}>
              <span>{group.title.toLocaleUpperCase()}</span>
              {group.description && <span>{group.description}</span>}
            </div>

            <div className={classes.root}>{fields}</div>
          </div>
        )
      })}
    </>
  )
}

export default CustomFieldsSection
